import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

  if (diffSecs < 60) {
    return rtf.format(-diffSecs, 'second');
  } else if (diffMins < 60) {
    return rtf.format(-diffMins, 'minute');
  } else if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  } else if (diffDays < 7) {
    return rtf.format(-diffDays, 'day');
  } else if (diffWeeks < 4) {
    return rtf.format(-diffWeeks, 'week');
  } else if (diffMonths < 12) {
    return rtf.format(-diffMonths, 'month');
  } else {
    return rtf.format(-diffYears, 'year');
  }
}

/**
 * Format a cron expression into human-readable text
 * @param cronExpression - Cron expression (e.g., "0 3 * * *")
 * @returns Human-readable schedule (e.g., "Setiap hari pukul 03:00")
 */
export function formatCronExpression(cronExpression: string): string {
  try {
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) {
      return cronExpression; // Return as-is if invalid format
    }

    const [minute, hour, dayOfMonth, _month, dayOfWeek] = parts;

    // Parse time
    const hourNum = hour === '*' ? 0 : parseInt(hour);
    const minuteNum = minute === '*' ? 0 : parseInt(minute);
    const timeStr = `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;

    // Day names in Indonesian
    const dayNames: Record<string, string> = {
      '0': 'Minggu',
      '1': 'Senin',
      '2': 'Selasa',
      '3': 'Rabu',
      '4': 'Kamis',
      '5': 'Jumat',
      '6': 'Sabtu',
    };

    // Daily pattern: * * * * *
    if (dayOfMonth === '*' && dayOfWeek === '*') {
      return `Setiap hari pukul ${timeStr}`;
    }

    // Weekly pattern: * * * * N
    if (dayOfMonth === '*' && dayOfWeek !== '*') {
      const dayName = dayNames[dayOfWeek] || `hari-${dayOfWeek}`;
      return `Setiap ${dayName} pukul ${timeStr}`;
    }

    // Monthly pattern: * * N * *
    if (dayOfMonth !== '*' && dayOfWeek === '*') {
      const day = parseInt(dayOfMonth);
      return `Setiap tanggal ${day} pukul ${timeStr}`;
    }

    // Complex pattern - return simplified version
    return `Terjadwal: ${timeStr}`;
  } catch (error) {
    return cronExpression;
  }
}
